// src/App.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext, AuthContextType, User } from './context/AuthContext';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import './App.css';

// Начальные пользователи для имитации (один user, один admin, один заблокированный)
const initialUsers: User[] = [
    { username: 'user', role: 'user', isBlocked: false },
    { username: 'admin', role: 'admin', isBlocked: false },
    { username: 'blockeduser', role: 'user', isBlocked: true },
];

function App() {
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [currentUser, setCurrentUser] = useState<User | null>(() => {
        try {
            const storedUser = localStorage.getItem('user');
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (error) {
            console.error("Failed to parse user from localStorage", error);
            return null;
        }
    });

    // Состояние для хранения всех пользователей
    const [allUsers, setAllUsers] = useState<User[]>(() => {
        try {
            const storedUsers = localStorage.getItem('allUsers');
            // Если пользователей нет, инициализируем начальными
            return storedUsers ? JSON.parse(storedUsers) : initialUsers;
        } catch (error) {
            console.error("Failed to parse allUsers from localStorage", error);
            return initialUsers;
        }
    });

    // Эффект для сохранения allUsers в localStorage при изменении
    useEffect(() => {
        try {
            localStorage.setItem('allUsers', JSON.stringify(allUsers));
        } catch (error) {
            console.error("Failed to save allUsers to localStorage", error);
        }
    }, [allUsers]);


    // Функция для входа
    const login = (newToken: string, username: string, role: 'user' | 'admin') => {
        // Проверяем, существует ли пользователь и не заблокирован ли он
        const foundUser = allUsers.find(u => u.username === username);
        if (foundUser && foundUser.isBlocked) {
            // В случае реального бэкенда, это должна быть ошибка от сервера.
            // Здесь мы просто не логиним.
            console.warn(`User ${username} is blocked.`);
            return;
        }

        localStorage.setItem('token', newToken);
        const loggedInUser: User = { username, role, isBlocked: false }; // isBlocked будет обновляться из allUsers
        // Ищем актуальный статус isBlocked из allUsers
        const actualUser = allUsers.find(u => u.username === username);
        if (actualUser) {
            loggedInUser.isBlocked = actualUser.isBlocked;
        }

        localStorage.setItem('user', JSON.stringify(loggedInUser));
        setToken(newToken);
        setCurrentUser(loggedInUser);
    };

    // Функция для выхода
    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setCurrentUser(null);
    };

    // Функция для регистрации нового пользователя
    const registerUser = (username: string, role: 'user' | 'admin') => {
        if (allUsers.some(u => u.username === username)) {
            return { success: false, message: 'Пользователь с таким именем уже существует.' };
        }
        const newUser: User = { username, role, isBlocked: false };
        setAllUsers(prevUsers => [...prevUsers, newUser]);
        return { success: true, message: 'Пользователь успешно зарегистрирован.' };
    };

    // Функция для обновления статуса пользователя (блокировка/разблокировка)
    const updateUserStatus = (username: string, isBlocked: boolean) => {
        setAllUsers(prevUsers =>
            prevUsers.map(u =>
                u.username === username ? { ...u, isBlocked: isBlocked } : u
            )
        );
        // Если текущий залогиненный пользователь блокируется, выходим из системы
        if (currentUser?.username === username && isBlocked) {
            logout();
        }
    };

    // Функция для удаления пользователя
    const deleteUser = (username: string) => {
        if (currentUser?.username === username) {
            // Нельзя удалить самого себя
            return;
        }
        setAllUsers(prevUsers => prevUsers.filter(u => u.username !== username));
        // Если удаляем залогиненного пользователя, но не самого себя (см. выше),
        // это значит, что кто-то другой его удалил, и нужно выйти из системы
        // (хотя в нашей имитации это не приведет к ошибке, если он уже вышел)
    };

    // Функция для получения всех списков ключевых слов конкретного пользователя
    const getUserBasicLists = (username: string) => {
        // В реальном приложении здесь был бы запрос к бэкенду.
        // В нашей имитации, списки хранятся в localStorage каждого пользователя
        // под ключом 'basicKeywordsLists'.
        // Чтобы их получить, нам нужно имитировать доступ к localStorage другого пользователя.
        // Это не совсем реалистично для реального приложения, но для демонстрации подойдет.
        // В реальном приложении админ запросил бы списки пользователя через API.
        const storedLists = localStorage.getItem(`basicKeywordsLists_${username}`); // Используем уникальный ключ для каждого пользователя
        return storedLists ? JSON.parse(storedLists) : [];
    };


    // Значение контекста, которое будет предоставлено всем дочерним компонентам
    const authContextValue: AuthContextType = useMemo(() => ({
        token,
        user: currentUser, // Используем currentUser
        allUsers,
        login,
        logout,
        registerUser,
        updateUserStatus,
        deleteUser,
        getUserBasicLists,
    }), [token, currentUser, allUsers]);

    return (
        <AuthContext.Provider value={authContextValue}>
            <Router>
                <Routes>
                    <Route path="/login" element={<AuthPage />} />
                    {/* Защищенный маршрут для Dashboard */}
                    <Route
                        path="/dashboard"
                        element={token && currentUser && !currentUser.isBlocked ? <DashboardPage /> : <Navigate to="/login" replace />}
                    />
                    {/* Защищенный маршрут для Admin (доступ только админам) */}
                    <Route
                        path="/admin"
                        element={token && currentUser?.role === 'admin' && !currentUser.isBlocked ? <AdminPage /> : <Navigate to="/login" replace />}
                    />
                    {/* Перенаправление на Dashboard или Login по умолчанию */}
                    <Route
                        path="*"
                        element={token && currentUser && !currentUser.isBlocked ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />}
                    />
                </Routes>
            </Router>
        </AuthContext.Provider>
    );
}

export default App;